/**
 * FOIA (Freedom of Information Act) search – government agency documents.
 *
 * When EXPO_PUBLIC_FOIA_API_KEY is set, uses the FOIA.gov API (agency_components).
 * Get a key at https://www.foia.gov/developer/
 *
 * The API returns federal agencies/components; results link to FOIA.gov for
 * requesting documents. Document-level search may require additional APIs.
 *
 * TROUBLESHOOTING "No results":
 * 1. Open DevTools → Console. In dev builds you'll see [FOIA] logs for each request.
 * 2. Open DevTools → Network. Filter by "foia" or "agency_components". Check:
 *    - Is the GET request sent (after OPTIONS)? Status 200, 400, 401, 403?
 *    - Response body: does it have "data": [] or "data": [{...}]?
 * 3. Ensure EXPO_PUBLIC_FOIA_API_KEY is set in the environment that builds the app
 *    (e.g. in your host's env for shxre.net so production has the key).
 * 4. Try "All agencies" and a short query like "FOIA" or "request" to see if any path returns data.
 */
import {useQuery} from '@tanstack/react-query'

import {FOIA_API_KEY} from '#/env/common'
import {STALE} from '#/state/queries'

const FOIA_SEARCH_ROOT = 'foia-search'
const FOIA_API_BASE = 'https://api.foia.gov/api'

const FOIA_DEBUG = typeof __DEV__ !== 'undefined' && __DEV__
function foiaLog(msg: string, data?: unknown) {
  if (FOIA_DEBUG) {
    if (data !== undefined) console.log('[FOIA]', msg, data)
    else console.log('[FOIA]', msg)
  }
}

export type FOIAResult = {
  id: string
  title: string
  agency: string
  agencyAbbreviation?: string
  /** Parent agency id (for filtering by agency dropdown) */
  agencyId?: string
  date: string
  snippet: string
  body: string
  sourceUrl?: string
}

export type FOIAAgency = {
  id: string
  name: string
  abbreviation?: string
}

/** FOIA.gov JSON:API response (agency_components + included agencies) */
type FOIAJsonApiResponse = {
  data?: Array<{
    type: string
    id: string
    attributes?: {
      title?: string
      abbreviation?: string
      body?: { value?: string }
      created?: string
    }
    relationships?: {
      agency?: { data?: { type: string; id: string } }
    }
  }>
  included?: Array<{
    type: string
    id: string
    attributes?: {
      name?: string
      abbreviation?: string
    }
  }>
}

function mapApiResultsToFOIAResults(
  data: FOIAJsonApiResponse['data'] = [],
  included: FOIAJsonApiResponse['included'] = [],
): FOIAResult[] {
  const agenciesById = new Map(
    (included ?? [])
      .filter((i) => i.type === 'agency')
      .map((i) => [i.id, i]),
  )
  return (data ?? []).map((comp) => {
    const agencyId = comp.relationships?.agency?.data?.id
    const agency = agencyId ? agenciesById.get(agencyId) : undefined
    const title = comp.attributes?.title ?? 'FOIA component'
    const agencyName = agency?.attributes?.name ?? 'Federal agency'
    const agencyAbbrev = agency?.attributes?.abbreviation ?? comp.attributes?.abbreviation
    const bodyText =
      (comp.attributes?.body && typeof comp.attributes.body === 'object' && comp.attributes.body.value) ||
      comp.attributes?.body ||
      ''
    const snippet = bodyText ? bodyText.slice(0, 200) + (bodyText.length > 200 ? '…' : '') : title
    return {
      id: comp.id,
      title,
      agency: agencyName,
      agencyAbbreviation: agencyAbbrev,
      agencyId: agencyId ?? undefined,
      date: comp.attributes?.created?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
      snippet,
      body: bodyText || snippet,
      sourceUrl: 'https://www.foia.gov',
    }
  })
}

/** Fetch agency_components (optionally filtered by agency), then filter by query client-side. */
async function fetchFOIAClientFilter(
  q: string,
  agencyId: string | null,
): Promise<FOIAResult[]> {
  const params = new URLSearchParams()
  params.set('include', 'agency')
  params.set('fields[agency]', 'name,abbreviation')
  params.set('fields[agency_component]', 'title,abbreviation,body,created,agency')
  params.set('page[limit]', '100')
  if (agencyId) params.set('filter[agency]', agencyId)
  const url = `${FOIA_API_BASE}/agency_components?${params.toString()}`
  foiaLog('clientFilter request', { url, agencyId, q })
  const res = await fetch(url, {
    headers: { 'X-API-Key': FOIA_API_KEY!, Accept: 'application/vnd.api+json' },
  })
  foiaLog('clientFilter response', { status: res.status, ok: res.ok })
  if (!res.ok) {
    if (agencyId && (res.status === 400 || res.status === 422)) {
      foiaLog('retrying without filter[agency]')
      params.delete('filter[agency]')
      const retry = await fetch(
        `${FOIA_API_BASE}/agency_components?${params.toString()}`,
        { headers: { 'X-API-Key': FOIA_API_KEY!, Accept: 'application/vnd.api+json' } },
      )
      if (!retry.ok) return []
      const json = (await retry.json()) as FOIAJsonApiResponse
      let all = mapApiResultsToFOIAResults(json.data, json.included)
      all = all.filter(r => r.agencyId === agencyId)
      const qLower = q.toLowerCase()
      return all.filter(
        (r) =>
          r.title.toLowerCase().includes(qLower) ||
          r.agency.toLowerCase().includes(qLower) ||
          (r.agencyAbbreviation && r.agencyAbbreviation.toLowerCase().includes(qLower)) ||
          r.snippet.toLowerCase().includes(qLower) ||
          r.body.toLowerCase().includes(qLower),
      )
    }
    return []
  }
  const json = (await res.json()) as FOIAJsonApiResponse
  let all = mapApiResultsToFOIAResults(json.data, json.included)
  foiaLog('clientFilter raw count', { beforeAgencyFilter: all.length })
  if (agencyId) all = all.filter(r => r.agencyId === agencyId)
  foiaLog('clientFilter after agency filter', { count: all.length, agencyId })
  const qLower = q.toLowerCase()
  const out = all.filter(
    (r) =>
      r.title.toLowerCase().includes(qLower) ||
      r.agency.toLowerCase().includes(qLower) ||
      (r.agencyAbbreviation && r.agencyAbbreviation.toLowerCase().includes(qLower)) ||
      r.snippet.toLowerCase().includes(qLower) ||
      r.body.toLowerCase().includes(qLower),
  )
  foiaLog('clientFilter after query filter', { count: out.length, q })
  return out
}

/** Sanitize search query: trim and remove trailing backslashes etc. that break the API filter. */
function sanitizeFOIAQuery(query: string): string {
  return query.trim().replace(/\s*\\+$/, '').trim()
}

async function fetchFOIAFromApi(query: string, agencyId: string | null): Promise<FOIAResult[]> {
  const q = sanitizeFOIAQuery(query)
  if (!q || !FOIA_API_KEY) return []

  const params = new URLSearchParams()
  params.set('include', 'agency')
  params.set('fields[agency]', 'name,abbreviation')
  params.set('fields[agency_component]', 'title,abbreviation,body,created,agency')
  params.set('page[limit]', '25')
  // Drupal JSON:API: CONTAINS for substring search (see docs/FOIA_API_REFERENCE.md)
  params.set('filter[title][operator]', 'CONTAINS')
  params.set('filter[title][value]', q)

  const url = `${FOIA_API_BASE}/agency_components?${params.toString()}`
  foiaLog('api request', { url, q, agencyId })
  const res = await fetch(url, {
    headers: { 'X-API-Key': FOIA_API_KEY, Accept: 'application/vnd.api+json' },
  })
  foiaLog('api response', { status: res.status, ok: res.ok })

  if (!res.ok) {
    if (res.status === 400 || res.status === 422) {
      foiaLog('api 4xx, falling back to clientFilter')
      return fetchFOIAClientFilter(q, agencyId)
    }
    return []
  }

  const json = (await res.json()) as FOIAJsonApiResponse
  let results = mapApiResultsToFOIAResults(json.data, json.included)
  foiaLog('api parsed', { rawCount: (json.data ?? []).length, mappedCount: results.length })
  if (agencyId) {
    results = results.filter(r => r.agencyId === agencyId)
  }
  // When API returns 200 but no results (filter ignored or no matches), use client-side filter
  if (results.length === 0) {
    foiaLog('api 0 results after agency filter, falling back to clientFilter')
    return fetchFOIAClientFilter(q, agencyId)
  }
  return results
}

/** Fetch list of agencies for dropdown. Uses /api/agency if available, else derives from agency_components. */
async function fetchFOIAAgencies(): Promise<FOIAAgency[]> {
  if (!FOIA_API_KEY) return getMockAgencies()

  try {
    const params = new URLSearchParams()
    params.set('fields[agency]', 'name,abbreviation')
    params.set('page[limit]', '500')
    const url = `${FOIA_API_BASE}/agency?${params.toString()}`
    const res = await fetch(url, {
      headers: { 'X-API-Key': FOIA_API_KEY, Accept: 'application/vnd.api+json' },
    })
    if (res.ok) {
      const json = (await res.json()) as { data?: Array<{ id: string; attributes?: { name?: string; abbreviation?: string } }> }
      const list = (json.data ?? []).map(a => ({
        id: a.id,
        name: a.attributes?.name ?? 'Agency',
        abbreviation: a.attributes?.abbreviation,
      }))
      list.sort((a, b) => a.name.localeCompare(b.name))
      return list
    }
  } catch {
    // Fall through to derived list
  }

  try {
    const params = new URLSearchParams()
    params.set('include', 'agency')
    params.set('fields[agency]', 'name,abbreviation')
    params.set('fields[agency_component]', 'agency')
    params.set('page[limit]', '100')
    const res = await fetch(`${FOIA_API_BASE}/agency_components?${params.toString()}`, {
      headers: { 'X-API-Key': FOIA_API_KEY, Accept: 'application/vnd.api+json' },
    })
    if (!res.ok) return getMockAgencies()
    const json = (await res.json()) as FOIAJsonApiResponse
    const agencies = (json.included ?? [])
      .filter((i): i is typeof i & { attributes: { name?: string; abbreviation?: string } } => i.type === 'agency')
      .map(i => ({ id: i.id, name: i.attributes?.name ?? 'Agency', abbreviation: i.attributes?.abbreviation }))
    const byId = new Map(agencies.map(a => [a.id, a]))
    const list = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
    return list.length > 0 ? list : getMockAgencies()
  } catch {
    return getMockAgencies()
  }
}

function getMockAgencies(): FOIAAgency[] {
  return [
    { id: 'doe', name: 'Department of Example', abbreviation: 'DOE' },
    { id: 'cia', name: 'Central Intelligence Agency', abbreviation: 'CIA' },
    { id: 'fbi', name: 'Federal Bureau of Investigation', abbreviation: 'FBI' },
    { id: 'dod', name: 'Department of Defense', abbreviation: 'DOD' },
    { id: 'epa', name: 'Environmental Protection Agency', abbreviation: 'EPA' },
    { id: 'state', name: 'Department of State', abbreviation: 'State' },
    { id: 'doj', name: 'Department of Justice', abbreviation: 'DOJ' },
    { id: 'nsa', name: 'National Security Agency', abbreviation: 'NSA' },
    { id: 'eop', name: 'Executive Office of the President', abbreviation: 'EOP' },
    { id: 'nara', name: 'National Archives and Records Administration', abbreviation: 'NARA' },
    { id: 'congress', name: 'U.S. Congress', abbreviation: 'Congress' },
  ]
}

async function fetchFOIASearch(query: string, agencyId: string | null): Promise<FOIAResult[]> {
  const q = sanitizeFOIAQuery(query)
  if (!q) return []
  foiaLog('search', { hasKey: !!FOIA_API_KEY, query: q, agencyId })

  if (FOIA_API_KEY) {
    try {
      const results = await fetchFOIAFromApi(query, agencyId)
      foiaLog('search result count', results.length)
      return results
    } catch (e) {
      foiaLog('search error (network/parse)', e)
      // Fall through to mock data on network/parse error
    }
  }

  // Fallback: mock results when no key or API returned nothing
  const qLower = q.toLowerCase()
  const allMockDocs: FOIAResult[] = [
    {
      id: '1',
      title: 'Sample FOIA Request Log – Fiscal Year 2023',
      agency: 'Department of Example',
      agencyAbbreviation: 'DOE',
      date: '2023-06-15',
      snippet:
        'Summary of FOIA requests received and processed in Q2 2023. Includes request categories and disposition.',
      body:
        'This is a placeholder document. Connect the FOIA.gov API in src/state/queries/foia.ts to load real agency documents.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'cia-1',
      title: 'Central Intelligence Agency – FOIA Reading Room',
      agency: 'Central Intelligence Agency',
      agencyAbbreviation: 'CIA',
      date: '2024-01-01',
      snippet:
        'The CIA is subject to FOIA and maintains a FOIA reading room with previously released documents.',
      body:
        'The Central Intelligence Agency (CIA) is a federal agency subject to the Freedom of Information Act. The CIA FOIA program processes requests and publishes previously released records in its electronic reading room.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'fbi-1',
      title: 'FBI FOIA – Frequently Requested Documents',
      agency: 'Federal Bureau of Investigation',
      agencyAbbreviation: 'FBI',
      date: '2024-02-14',
      snippet:
        'FBI FOIA reading room and vault. Previously released records and frequently requested documents.',
      body:
        'The FBI maintains a FOIA Electronic Reading Room and Vault with documents that have been released under the Freedom of Information Act.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'dod-1',
      title: 'Department of Defense – FOIA Annual Report',
      agency: 'Department of Defense',
      agencyAbbreviation: 'DOD',
      date: '2023-12-01',
      snippet:
        'DOD FOIA annual report: requests received, processed, and backlog statistics.',
      body:
        'The Department of Defense FOIA program handles requests for agency records. This placeholder represents an annual report or reading room document.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'epa-1',
      title: 'EPA FOIA Log – Environmental Compliance',
      agency: 'Environmental Protection Agency',
      agencyAbbreviation: 'EPA',
      date: '2024-01-20',
      snippet:
        'EPA FOIA request log including environmental compliance and enforcement records.',
      body:
        'The Environmental Protection Agency releases documents under FOIA including compliance and enforcement records.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'state-1',
      title: 'State Department – FOIA Reading Room',
      agency: 'Department of State',
      agencyAbbreviation: 'State',
      date: '2023-11-10',
      snippet:
        'State Department FOIA releases and declassified documents.',
      body:
        'The Department of State maintains a FOIA reading room with released and declassified documents.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'doj-1',
      title: 'DOJ FOIA – Guide and Request Log',
      agency: 'Department of Justice',
      agencyAbbreviation: 'DOJ',
      date: '2024-03-01',
      snippet:
        'Department of Justice FOIA guide and summary of requests processed.',
      body:
        'The DOJ Office of Information Policy oversees FOIA compliance across the government and publishes guidance and logs.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'nsa-1',
      title: 'NSA FOIA – Declassification and Releases',
      agency: 'National Security Agency',
      agencyAbbreviation: 'NSA',
      date: '2023-09-15',
      snippet:
        'NSA FOIA releases and declassification program documents.',
      body:
        'The National Security Agency processes FOIA requests and releases documents in accordance with the law.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'wh-1',
      title: 'White House FOIA – Presidential Records and Communications',
      agency: 'Executive Office of the President',
      agencyAbbreviation: 'EOP',
      date: '2024-02-01',
      snippet:
        'White House FOIA requests and releases. Presidential records, communications, and visitor logs.',
      body:
        'The Executive Office of the President processes FOIA requests for presidential and White House records. This placeholder represents the kind of documents available via FOIA from the White House.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'trump-1',
      title: 'DOJ – FOIA Releases Related to Trump Administration (2017–2021)',
      agency: 'Department of Justice',
      agencyAbbreviation: 'DOJ',
      date: '2024-01-15',
      snippet:
        'Department of Justice FOIA releases and request logs pertaining to the executive branch and Trump administration matters.',
      body:
        'The Department of Justice has released documents under FOIA related to executive branch activities. Search FOIA.gov or agency reading rooms for specific releases. This is placeholder text; connect a real FOIA API for live results.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'trump-2',
      title: 'National Archives – Trump Presidential Records and FOIA',
      agency: 'National Archives and Records Administration',
      agencyAbbreviation: 'NARA',
      date: '2023-12-20',
      snippet:
        'NARA FOIA program. Trump presidential records, White House materials, and federal agency records.',
      body:
        'The National Archives processes FOIA requests for presidential and federal records. Presidential records are subject to the Presidential Records Act and FOIA. This is placeholder text for demo purposes.',
      sourceUrl: 'https://www.foia.gov',
    },
    {
      id: 'congress-1',
      title: 'Congressional Records and FOIA – House and Senate',
      agency: 'U.S. Congress',
      agencyAbbreviation: 'Congress',
      date: '2023-10-01',
      snippet:
        'Congressional offices and support agencies subject to FOIA. House and Senate records, reports, and communications.',
      body:
        'Certain congressional support agencies and records may be subject to FOIA. This placeholder represents the type of documents that can be requested. Connect FOIA.gov API for real search.',
      sourceUrl: 'https://www.foia.gov',
    },
  ]

  // 1) Keyword match (agency names, etc.)
  const keywords: [string[], string][] = [
    [['cia', 'central intelligence'], 'cia-1'],
    [['fbi', 'federal bureau'], 'fbi-1'],
    [['dod', 'defense', 'military'], 'dod-1'],
    [['epa', 'environmental', 'environment'], 'epa-1'],
    [['state', 'department of state', 'diplomacy'], 'state-1'],
    [['doj', 'justice', 'attorney general'], 'doj-1'],
    [['nsa', 'national security agency'], 'nsa-1'],
    [['white house', 'eop', 'president', 'executive'], 'wh-1'],
    [['trump', 'presidential', 'nara', 'archives', '2017', '2021'], 'trump-1'],
    [['trump', 'nara', 'archives', 'presidential records'], 'trump-2'],
    [['congress', 'house', 'senate', 'capitol'], 'congress-1'],
  ]
  const keywordMatchedIds = new Set<string>()
  for (const [terms, id] of keywords) {
    if (terms.some(term => qLower.includes(term))) keywordMatchedIds.add(id)
  }

  // 2) Free-text match: query appears in title, snippet, agency, or body (search at will)
  const textMatches = allMockDocs.filter(d => {
    const searchable = [
      d.title,
      d.snippet,
      d.agency,
      d.agencyAbbreviation ?? '',
      d.body,
    ]
      .join(' ')
      .toLowerCase()
    return searchable.includes(qLower)
  })
  const textMatchIds = new Set(textMatches.map(d => d.id))

  // Combine: keyword matches first, then text matches, then rest; dedupe
  const matchedIds = new Set([...keywordMatchedIds, ...textMatchIds])
  const matching = allMockDocs.filter(d => matchedIds.has(d.id))
  const rest = allMockDocs.filter(d => !matchedIds.has(d.id))
  let combined = matching.length > 0 ? [...matching, ...rest] : rest
  if (agencyId) {
    const agencyName = getMockAgencies().find(a => a.id === agencyId)?.name
    if (agencyName) combined = combined.filter(d => d.agency === agencyName)
  }
  const minResults = 4
  return combined.slice(0, Math.max(minResults, matching.length + 2))
}

const FOIA_AGENCIES_ROOT = 'foia-agencies'

export function useFOIAAgenciesQuery() {
  return useQuery({
    queryKey: [FOIA_AGENCIES_ROOT],
    queryFn: fetchFOIAAgencies,
    staleTime: STALE.MINUTES.FIVE,
  })
}

export function useFOIASearchQuery(query: string, agencyId: string | null = null) {
  return useQuery({
    queryKey: [FOIA_SEARCH_ROOT, query.trim(), agencyId ?? ''],
    queryFn: () => fetchFOIASearch(query, agencyId),
    staleTime: STALE.MINUTES.FIVE,
    enabled: query.trim().length > 0,
  })
}
